import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Link, Outlet } from 'react-router-dom';
import { CircularProgress, Divider, IconButton, Stack, Typography, useTheme } from '@mui/material';

import {
  ProcessResponseDTO,
  ProcessResponseDTOAssessmentType,
  ProcessResponseDTOStatus,
  useGetProcessById
} from '@/api/generated';
import BriefcaseIcon from '@/assets/icons/briefcase.svg?react';
import CheckIcon from '@/assets/icons/check.svg?react';
import SettingsIcon from '@/assets/icons/settings.svg?react';
import GeneralStatusDropdownSelect from '@/components/fields/GeneralStatusDropdownSelect';
import LocationDisplay from '@/components/fields/Location/LocationDisplay';
import HeaderProperty from '@/components/HeaderProperty';
import Loader from '@/components/Loader/Loader';
import LoadingButton from '@/components/LoadingButton';
import ProcessActionMenu from '@/components/ProcessActionMenu';
import SeparatedList from '@/components/SeparatedList';
import { useProcessStatusActions } from '@/hooks/useProcessStatusActions';
import { toProcessSettingsForm } from '@/root/linker';
import { borders } from '@/theme/constants';
import typography from '@/theme/typography';

const noOp = () => {};

const ProcessHeader = ({ processId }: { processId: string }) => {
  const queryClient = useQueryClient();

  const { data: process, queryKey: processQueryKey } = useGetProcessById(processId);
  const processStatus = process?.status as string;

  const setProcessStatus = useCallback(
    (newProcessStatus: ProcessResponseDTOStatus | undefined) => {
      if (!process) return;

      queryClient.setQueryData(
        processQueryKey,
        (oldData: ProcessResponseDTO | undefined) =>
          ({ ...oldData!, status: newProcessStatus }) satisfies ProcessResponseDTO
      );
    },
    [queryClient, processQueryKey, process]
  );

  const {
    publish: handlePublish,
    isLoading: isStatusLoading,
    isPublishAvailable,
    availableStatuses
  } = useProcessStatusActions({
    currentStatus: process?.status,
    onChange: setProcessStatus,
    processId
  });

  const { t } = useTranslation(['processes', 'common']);
  const { spacing } = useTheme();
  const processStatusWithFallback = processStatus || ProcessResponseDTOStatus.UNPUBLISHED;
  const isActive = processStatus === ProcessResponseDTOStatus.ACTIVE;

  const processType = process?.assessmentType || ProcessResponseDTOAssessmentType.ANY;

  // TODO: https://github.com/MidotGit/podium-frontend/pull/76#discussion_r1901058107
  if (!process) {
    return <Loader />;
  }

  return (
    <Stack p={4} gap={2}>
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row" alignItems="center" gap={1}>
          <BriefcaseIcon />
          <Typography variant="h2">{process.name}</Typography>
          {/* // TODO: https://github.com/MidotGit/podium-frontend/pull/76#discussion_r1900781800 */}
          <GeneralStatusDropdownSelect
            name="status"
            onChange={noOp}
            value={processStatus}
            activeOptions={availableStatuses as string[]}
            isDisabled
            sx={{
              mx: 2,
              '& .DropdownSelect-control': {
                minWidth: 122,
                minHeight: 'unset',
                '& .DropdownSelect-optionIcon .CircleIcon-root': {
                  m: 0.25
                },
                '& .DropdownSelect-optionText .MuiTypography-root': {
                  ...typography.body3,
                  lineHeight: 'calc(4 / 3)'
                }
              },
              '& .DropdownSelect-indicator': {
                display: 'none'
              }
            }}
          />
          {isStatusLoading && <CircularProgress size={20} />}
        </Stack>
        <Stack direction="row" alignItems="center" gap={2}>
          <LoadingButton
            variant={isPublishAvailable ? 'contained' : 'text'}
            sx={{
              p: spacing(0.5, 1.5),
              '& .MuiButton-startIcon .CheckIcon-root': {
                width: spacing(2.5),
                height: spacing(2.5),
                marginInlineEnd: spacing(1)
              },
              '&.Mui-disabled': {
                bgcolor: 'transparent',
                color: 'primary.main'
              }
            }}
            isLoading={isStatusLoading}
            loadingIndicator={<CircularProgress size={20} />}
            onClick={handlePublish}
            disabled={!isPublishAvailable}
            startIcon={isActive && !isStatusLoading && <CheckIcon className="CheckIcon-root" />}
          >
            {isPublishAvailable && t('layouts.processes.details.info.action.publish')}
            {!isPublishAvailable &&
              t(`layouts.processes.details.info.state.${processStatusWithFallback.toLowerCase()}`)}
          </LoadingButton>
          <>
            <IconButton
              to={toProcessSettingsForm({ processId })}
              component={Link}
              sx={{ border: borders.half, borderColor: 'grey.400', borderRadius: spacing(0.5) }}
            >
              <SettingsIcon />
            </IconButton>
            <Outlet />
          </>
          <ProcessActionMenu process={process!} onStatusChange={setProcessStatus} />
        </Stack>
      </Stack>
      <SeparatedList
        separator={<Divider orientation="vertical" flexItem sx={{ m: spacing(0, 3) }} />}
        sx={{
          '& .HeaderProperty-root': { m: 0 },
          alignItems: 'center'
        }}
      >
        <HeaderProperty variant="body3">
          <LocationDisplay organization={process?.organization} location={process?.location} outlined />
        </HeaderProperty>

        <HeaderProperty
          variant="body3"
          label={t(`layouts.processes.details.info.processTypes.${processType?.toLowerCase()}`)}
        />
      </SeparatedList>
    </Stack>
  );
};

export default ProcessHeader;
